<template>
  <!-- NOTE(tsuburaya): Fork from https://github.com/lokyoung/vuejs-paginate -->
  <ul class="flex items-center gap-x-4">
    <!-- 前へ戻るボタン -->
    <li class="w-10">
      <a class="cursor-pointer justify-center" :class="[firstPageSelected ? 'hidden' : 'flex']" :tabindex="firstPageSelected ? -1 : 0" @click="onClickPrevPage()" @keyup.enter="onClickPrevPage()">
        <svg fill="none" height="12" viewBox="0 0 7 12" width="7" xmlns="http://www.w3.org/2000/svg">
          <path class="stroke-gold1" d="M6.05306 11.165L0.719727 5.83171L6.05306 0.498372" />
        </svg>
      </a>
    </li>
    <!-- 数値のループ -->
    <li v-for="page in pages" :key="page.index" :class="[page.breakView ? '' : '']">
      <!-- ... を表示するとき -->
      <AtomsParagraph v-if="page.breakView" class="text-gray2" tabindex="0"><slot name="breakViewContent">…</slot></AtomsParagraph>
      <!-- 数値を表示するとき -->
      <button
        v-else
        class="h-10 w-10 border text-sm"
        tabindex="0"
        :class="[{ 'border-beige1 bg-beige1 text-gold1': page.selected }, { 'border-gray2 text-gray2': !page.selected }]"
        @click="() => handlePageSelected(page.index + 1)"
      >
        {{ page.content }}
      </button>
    </li>
    <!-- 次へ進むボタン -->
    <li class="w-10">
      <a class="cursor-pointer justify-center" :class="[lastPageSelected ? 'hidden' : 'flex']" :tabindex="lastPageSelected ? -1 : 0" @click="onClickNextPage()" @keyup.enter="onClickNextPage()">
        <svg fill="none" height="16" viewBox="0 0 9 16" width="9" xmlns="http://www.w3.org/2000/svg">
          <path class="stroke-gold1" d="M0.665854 1.33325L7.33252 7.99992L0.665854 14.6666" />
        </svg>
      </a>
    </li>
  </ul>
</template>

<script lang="ts" setup>
interface Props {
  selectedPageNum: number;
  pageCount: number;
  onClick: (index: number) => void;
  pageRange?: number;
  marginPages?: number;
}

const props = withDefaults(defineProps<Props>(), {
  pageRange: 3,
  marginPages: 1,
});

interface Page {
  index: number;
  content: string;
  selected: boolean;

  breakView: boolean;
  disabled: boolean;
}

const pages = computed(() => {
  const items: Page[] = [];

  if (props.pageCount <= props.pageRange) {
    for (let i = 0; i < props.pageCount; i++) {
      const page = {
        index: i,
        content: (i + 1).toString(),
        selected: i === props.selectedPageNum - 1,
        breakView: false,
        disabled: false,
      };
      items[i] = page;
    }
  } else {
    const halfPageRange = Math.floor(props.pageRange / 2);

    const setPageItem = (index: number) => {
      const page = {
        index,
        content: (index + 1).toString(),
        selected: index === props.selectedPageNum - 1,
        breakView: false,
        disabled: false,
      };
      items[index] = page;
    };

    const setBreakView = (index: number) => {
      const breakView = {
        index,
        content: '',
        selected: false,
        disabled: true,
        breakView: true,
      };

      items[index] = breakView;
    };

    // 1st - loop thru low end of margin pages
    for (let i = 0; i < props.marginPages; i++) {
      setPageItem(i);
    }

    // 2nd - loop thru selected range
    let selectedRangeLow = 0;
    if (props.selectedPageNum - halfPageRange > 0) {
      selectedRangeLow = props.selectedPageNum - 1 - halfPageRange;
    }

    let selectedRangeHigh = selectedRangeLow + props.pageRange - 1;
    if (selectedRangeHigh >= props.pageCount) {
      selectedRangeHigh = props.pageCount - 1;
      selectedRangeLow = selectedRangeHigh - props.pageRange + 1;
    }

    for (let i = selectedRangeLow; i <= selectedRangeHigh && i <= props.pageCount - 1; i++) {
      setPageItem(i);
    }

    // Check if there is breakView in the left of selected range
    if (selectedRangeLow > props.marginPages) {
      setBreakView(selectedRangeLow - 1);
    }

    // Check if there is breakView in the right of selected range
    if (selectedRangeHigh + 1 < props.pageCount - props.marginPages) {
      setBreakView(selectedRangeHigh + 1);
    }

    // 3rd - loop thru high end of margin pages
    for (let i = props.pageCount - 1; i >= props.pageCount - props.marginPages; i--) {
      setPageItem(i);
    }
  }

  // null を排除する
  return items.filter((_) => _);
});

const handlePageSelected = (index: number) => {
  if (props.selectedPageNum === index) return;
  props.onClick(index);
};

const onClickPrevPage = () => {
  if (props.selectedPageNum <= 1) return;
  handlePageSelected(props.selectedPageNum - 1);
};

const onClickNextPage = () => {
  if (props.selectedPageNum >= props.pageCount) return;
  handlePageSelected(props.selectedPageNum + 1);
};

const firstPageSelected = computed<boolean>(() => props.selectedPageNum === 1);
const lastPageSelected = computed<boolean>(() => props.selectedPageNum === props.pageCount);
</script>
